import type {DashboardSidebar} from '@github-ui/shared-dashboard-sidebar/DashboardSidebar'
import type {ComponentProps} from 'react'

import {
  getRepoPullsCleanPath,
  type RepoPullsCleanPathQualifier,
  REPO_PULLS_DEFAULT_QUERY,
} from './repo-pulls-dashboard-content-route'

type RepoPullsSidebarLink = ComponentProps<typeof DashboardSidebar>['links'][number]

export const REPO_PULLS_SIDEBAR_LINKS = [
  {
    id: 'all',
    name: 'Pull requests',
    searchType: 'pull-requests',
    query: REPO_PULLS_DEFAULT_QUERY,
    icon: 'inbox',
    color: 'gray',
    source: 'predefined',
    preload: 'intent',
  },
  {
    id: 'authored',
    name: 'Authored by me',
    searchType: 'pull-requests',
    query: `${REPO_PULLS_DEFAULT_QUERY} author:@me`,
    icon: 'smiley',
    color: 'gray',
    source: 'predefined',
    preload: 'intent',
  },
  {
    id: 'assigned',
    name: 'Assigned to me',
    searchType: 'pull-requests',
    query: `${REPO_PULLS_DEFAULT_QUERY} assignee:@me`,
    icon: 'person',
    color: 'gray',
    source: 'predefined',
    preload: 'intent',
  },
  {
    id: 'involved',
    name: 'Involves me',
    searchType: 'pull-requests',
    query: `${REPO_PULLS_DEFAULT_QUERY} involves:@me`,
    icon: 'comment',
    color: 'gray',
    source: 'predefined',
    preload: 'intent',
  },
  {
    id: 'review-requested',
    name: 'Review requests',
    searchType: 'pull-requests',
    query: `${REPO_PULLS_DEFAULT_QUERY} review-requested:@me`,
    icon: 'git_pull_request',
    color: 'gray',
    source: 'predefined',
    preload: 'intent',
  },
] satisfies RepoPullsSidebarLink[]

type RepoPullsSidebarLinkId = (typeof REPO_PULLS_SIDEBAR_LINKS)[number]['id']
export type RepoPullsAnalyticsView = RepoPullsSidebarLinkId | 'custom'

export function normalizeRepoPullsQuery(query: string) {
  return query.trim().split(/\s+/).filter(Boolean).join(' ')
}

const SIDEBAR_LINK_ID_BY_CLEAN_PATH_QUALIFIER = {
  author: 'authored',
  assignee: 'assigned',
  mentions: 'involved',
  'review-requested': 'review-requested',
} as const satisfies Record<RepoPullsCleanPathQualifier, RepoPullsSidebarLinkId>

export function getSelectedRepoPullsSidebarLink(query: string, pathname?: string, currentUserLogin?: string) {
  const cleanPath = pathname ? getRepoPullsCleanPath(pathname) : undefined
  if (cleanPath) {
    const cleanPathSidebarLinkId =
      currentUserLogin?.toLowerCase() === cleanPath.value.toLowerCase()
        ? SIDEBAR_LINK_ID_BY_CLEAN_PATH_QUALIFIER[cleanPath.qualifier]
        : 'all'
    return REPO_PULLS_SIDEBAR_LINKS.find(link => link.id === cleanPathSidebarLinkId)
  }

  const normalizedQuery = normalizeRepoPullsQuery(query)
  return REPO_PULLS_SIDEBAR_LINKS.find(link => normalizeRepoPullsQuery(link.query) === normalizedQuery)
}

export function getRepoPullsAnalyticsView(
  query: string,
  pathname?: string,
  currentUserLogin?: string,
): RepoPullsAnalyticsView {
  const cleanPath = pathname ? getRepoPullsCleanPath(pathname) : undefined
  if (cleanPath) {
    return currentUserLogin?.toLowerCase() === cleanPath.value.toLowerCase()
      ? SIDEBAR_LINK_ID_BY_CLEAN_PATH_QUALIFIER[cleanPath.qualifier]
      : 'custom'
  }

  return getSelectedRepoPullsSidebarLink(query)?.id ?? 'custom'
}
